'use client';

import { c as _c } from "react/compiler-runtime";
import type { Reviewer } from '@/types/github';
import { RotateCw, X } from 'lucide-react';
import { reviewerStateDisplay } from './reviewer-state-icons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { BetterTooltip } from '@/components/ui/tooltip';
import { ReviewerState } from '@/types/github';
function getClickableStateCopy(reviewer: Reviewer): {
  ariaLabel: string;
  tooltip: string;
} | null {
  if (reviewer.state === ReviewerState.CHANGES_REQUESTED) {
    return {
      ariaLabel: `View rejection comment from ${reviewer.login}`,
      tooltip: 'Rejected. Click to see comments'
    };
  }
  if (reviewer.state === ReviewerState.COMMENTED) {
    return {
      ariaLabel: `View comment from ${reviewer.login}`,
      tooltip: 'Commented. Click to see comment'
    };
  }
  return null;
}
type ReviewerItemProps = {
  reviewer: Reviewer;
  onReRequest: (reviewer: Reviewer) => void;
  onDismiss: (reviewer: Reviewer) => void;
  canDismiss: boolean;
  canReRequest: boolean;
  actionIconClassName?: string;
  stateIconClassName?: string;
  onStateIconClick?: (reviewer: Reviewer) => void;
};
export function ReviewerItem(t0) {
  const $ = _c(49);
  const {
    reviewer,
    onReRequest,
    onDismiss,
    canDismiss,
    canReRequest,
    actionIconClassName,
    stateIconClassName,
    onStateIconClick
  } = t0;
  let t1;
  if ($[0] !== onStateIconClick || $[1] !== reviewer) {
    t1 = onStateIconClick && reviewer.reviewId ? getClickableStateCopy(reviewer) : null;
    $[0] = onStateIconClick;
    $[1] = reviewer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const clickableStateCopy = t1;
  let t2;
  if ($[3] !== clickableStateCopy || $[4] !== onStateIconClick || $[5] !== reviewer) {
    t2 = clickableStateCopy && onStateIconClick ? () => onStateIconClick(reviewer) : null;
    $[3] = clickableStateCopy;
    $[4] = onStateIconClick;
    $[5] = reviewer;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleStateIconClick = t2;
  const stateTooltip = reviewer.state === ReviewerState.DISMISSED && canReRequest ? "Review dismissed. Re-request review to ask again." : clickableStateCopy ? clickableStateCopy.tooltip : reviewerStateDisplay[reviewer.state].label;
  const t3 = reviewerStateDisplay[reviewer.state];
  let t4;
  if ($[7] !== t3.icon) {
    t4 = <>{t3.icon}</>;
    $[7] = t3.icon;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const stateIconContent = t4;
  const t5 = `${reviewer.login}'s avatar`;
  let t6;
  if ($[9] !== reviewer.avatarUrl || $[10] !== t5) {
    t6 = <AvatarImage src={reviewer.avatarUrl} alt={t5} />;
    $[9] = reviewer.avatarUrl;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== reviewer.login) {
    t7 = (reviewer.login ?? "").slice(0, 2).toUpperCase() || "NA";
    $[12] = reviewer.login;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <AvatarFallback>{t7}</AvatarFallback>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t8) {
    t9 = <Avatar className="size-5">{t6}{t8}</Avatar>;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== reviewer.login) {
    t10 = <span className="text-sm">{reviewer.login}</span>;
    $[19] = reviewer.login;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <div className="flex min-w-0 flex-1 items-center gap-2 text-zinc-300">{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== actionIconClassName || $[25] !== canReRequest || $[26] !== onReRequest || $[27] !== reviewer) {
    t12 = canReRequest && <BetterTooltip content="Re-request review" side="top"><Button variant="ghost" size="icon" className="size-6 text-zinc-500 opacity-0 transition-opacity group-hover:opacity-100" aria-label={`Re-request review from ${reviewer.login}`} onClick={() => onReRequest(reviewer)}><RotateCw className={actionIconClassName ?? "size-4"} /></Button></BetterTooltip>;
    $[24] = actionIconClassName;
    $[25] = canReRequest;
    $[26] = onReRequest;
    $[27] = reviewer;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== actionIconClassName || $[30] !== canDismiss || $[31] !== onDismiss || $[32] !== reviewer) {
    t13 = canDismiss && <BetterTooltip content="Dismiss review" side="top"><Button variant="ghost" size="icon" className="size-6 text-zinc-500 opacity-0 transition-opacity group-hover:opacity-100" aria-label={`Dismiss review from ${reviewer.login}`} onClick={() => onDismiss(reviewer)}><X className={actionIconClassName ?? "size-4"} /></Button></BetterTooltip>;
    $[29] = actionIconClassName;
    $[30] = canDismiss;
    $[31] = onDismiss;
    $[32] = reviewer;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== clickableStateCopy || $[35] !== handleStateIconClick || $[36] !== stateIconClassName || $[37] !== stateIconContent) {
    t14 = handleStateIconClick && clickableStateCopy ? <button type="button" className={`flex size-6 cursor-pointer items-center justify-center text-zinc-500 ${stateIconClassName ?? ""}`} aria-label={clickableStateCopy.ariaLabel} onClick={handleStateIconClick}>{stateIconContent}</button> : <span className={`flex size-6 cursor-pointer items-center justify-center text-zinc-500 ${stateIconClassName ?? ""}`}>{stateIconContent}</span>;
    $[34] = clickableStateCopy;
    $[35] = handleStateIconClick;
    $[36] = stateIconClassName;
    $[37] = stateIconContent;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== stateTooltip || $[40] !== t14) {
    t15 = <BetterTooltip content={stateTooltip} side="top">{t14}</BetterTooltip>;
    $[39] = stateTooltip;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t12 || $[43] !== t13 || $[44] !== t15) {
    t16 = <div className="flex shrink-0 items-center gap-2 pl-2 pr-0.5">{t12}{t13}{t15}</div>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t11 || $[47] !== t16) {
    t17 = <div className="group flex items-center justify-between transition-opacity">{t11}{t16}</div>;
    $[46] = t11;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
}